import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Users, ListChecks, Sparkles, Archive } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_magic-link-journey/artifacts/zmxbnelr_Reelty_Logo_R-Only.png";

function NavLink({ to, active, icon: Icon, children, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`font-mono-tag text-[11px] px-3 py-2 rounded-sm transition-colors inline-flex items-center gap-2 ${
        active
          ? "bg-[#6B0F1A] text-[#F5EBD9]"
          : "text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#1A0F0A]/5"
      }`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
      {children}
    </Link>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isAdmin = user?.role === "admin";

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F5EBD9] text-[#1A0F0A] grain-overlay" data-testid="app-shell">
      <header className="sticky top-0 z-40 border-b border-[#1A0F0A]/10 backdrop-blur bg-[#F5EBD9]/90">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-4 gap-4">
          <Link to={isAdmin ? "/admin" : "/dashboard"} className="flex items-center gap-3 shrink-0" data-testid="app-shell-logo">
            <img src={LOGO_URL} alt="Reelty" className="w-9 h-9 object-contain" />
            <span className="font-mono-tag text-[10px] text-[#78584B]">Onboarding Portal</span>
          </Link>

          {isAdmin && (
            <nav className="hidden md:flex items-center gap-1">
              <NavLink
                to="/admin"
                active={pathname === "/admin"}
                icon={Users}
                testId="nav-admin-roster"
              >
                Roster
              </NavLink>
              <NavLink
                to="/admin/applicants"
                active={pathname.startsWith("/admin/applicants")}
                icon={Sparkles}
                testId="nav-admin-applicants"
              >
                Applicants
              </NavLink>
              <NavLink
                to="/admin/templates"
                active={pathname.startsWith("/admin/templates")}
                icon={ListChecks}
                testId="nav-admin-templates"
              >
                Checklist
              </NavLink>
              <NavLink
                to="/admin/archive"
                active={pathname.startsWith("/admin/archive")}
                icon={Archive}
                testId="nav-admin-archive"
              >
                Archive
              </NavLink>
            </nav>
          )}

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-xs text-[#1A0F0A]" data-testid="app-shell-user-email">
                {user?.email}
              </span>
              <span className="font-mono-tag text-[9px] text-[#78584B]">
                {user?.role === "admin" ? "Admin" : "Photographer"}
              </span>
            </div>
            <Button
              onClick={handleLogout}
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              className="text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#1A0F0A]/5"
            >
              <LogOut className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10">{children}</main>
    </div>
  );
}
